import { JSONModel, toJSONContext } from '@/store/models/jsonModel';
import BeforeAfterImage, { BeforeAfterImageJSON } from './before-after-image';
import PracticeRelationship, { PracticeRelationshipJSON } from '@/store/modules/practices/practice-relationship';
import UserRelationship, { UserRelationshipJSON } from '@/store/modules/users/user-relationship';
import Taxonomy, { TaxonomyJSON } from '@/store/models/taxonomy';
import ProductRelationship, { ProductRelationshipJSON } from '@/store/modules/products/product-relationship';
import { BeforeAfterImagesStatus } from './types';
import config from '@/config';
export interface BeforeAfterJSON {
  id: number;
  status: BeforeAfterImagesStatus;
  practice_id: number;
  language_id: number;
  country_id: number;
  product_id: number;
  user_id: number;
  title: string;
  notes: string;
  created_at: string;
  updated_at: string;
  images: BeforeAfterImageJSON[];
  practice?: PracticeRelationshipJSON;
  user?: UserRelationshipJSON;
  treatments: TaxonomyJSON[];
  product?: ProductRelationshipJSON;
  products?: ProductRelationshipJSON[];
  zip?: string;
  consent: boolean;
  name: string;
  credentials: string;
  practice_name: string;
  email: string;
  phone_number: string;
  street: string;
  additional: string;
  city: string;
  state: string;
  postal_code: string;
  device_settings_used: string;
  number_of_treatments_performed: number | null;
  accessories?: TaxonomyJSON[];
  instagram_url?: string;
  facebook_url?: string;
  tiktok_url?: string;
}
export default class BeforeAfter extends JSONModel<BeforeAfterJSON> {
  public id: number = 0;
  public status: BeforeAfterImagesStatus = BeforeAfterImagesStatus.Active;
  public title: string = '';
  public notes: string = '';
  public createdAt: string = '';
  public updatedAt: string = '';
  public images: BeforeAfterImage[] = [new BeforeAfterImage()];
  public practice?: PracticeRelationship;
  public user?: UserRelationship;
  public treatments: Taxonomy[] = [];
  public product?: ProductRelationship;
  /** Multiple products; use this for new code. product is kept as first for backward compat. */
  public products: ProductRelationship[] = [];
  public zip: string = '';
  public consent: boolean = false;
  public name: string = '';
  public credentials: string = '';
  public practiceName: string = '';
  public email: string = '';
  public phoneNumber: string = '';
  public street: string = '';
  public additional: string = '';
  public city: string = '';
  public state: string = '';
  public postalCode: string = '';
  public deviceSettingsUsed: string = '';
  public numberOfTreatmentsPerformed: number | null = null;
  public accessories: Taxonomy[] = [];
  public instagramUrl: string = '';
  public facebookUrl: string = '';
  public tiktokUrl: string = '';
  get zipUrl(): string {
    if (this.zip !== '') {
      return config.API_HOSTNAME + this.zip.replace('/api', '');
    }
    return '';
  }
  public fromJSON(json: BeforeAfterJSON) {
    if (json.id) {
      this.id = Number(json.id);
    }
    if (json.status) {
      this.status =
        Number(json.status) === BeforeAfterImagesStatus.Active
          ? BeforeAfterImagesStatus.Active
          : BeforeAfterImagesStatus.Inactive;
    }
    if (json.title) {
      this.title = String(json.title);
    }
    if (json.notes) {
      this.notes = String(json.notes);
    }
    if (json.created_at) {
      this.createdAt = String(json.created_at);
    }
    if (json.updated_at) {
      this.updatedAt = String(json.updated_at);
    }
    if (json.images && Array.isArray(json.images)) {
      this.images = json.images.map((item) => BeforeAfterImage.fromJSON(item));
    }
    if (json.practice) {
      this.practice = PracticeRelationship.fromJSON(json.practice);
    }
    if (json.user) {
      this.user = UserRelationship.fromJSON(json.user);
    }
    if (json.treatments && Array.isArray(json.treatments)) {
      this.treatments = json.treatments.map((item) => Taxonomy.fromJSON(item));
    }
    if (json.products && Array.isArray(json.products) && json.products.length) {
      this.products = json.products.map((p) => ProductRelationship.fromJSON(p));
      this.product = this.products[0];
    } else if (json.product) {
      this.product = ProductRelationship.fromJSON(json.product);
      this.products = [this.product];
    } else {
      this.product = undefined;
      this.products = [];
    }
    if (json.zip) {
      this.zip = String(json.zip);
    }
    if (json.consent && Boolean(json.consent)) {
      this.consent = true;
    }
    if (json.name) {
      this.name = String(json.name);
    }
    if (json.credentials) {
      this.credentials = String(json.credentials);
    }
    if (json.practice_name) {
      this.practiceName = String(json.practice_name);
    }
    if (json.email) {
      this.email = String(json.email);
    }
    if (json.phone_number) {
      this.phoneNumber = String(json.phone_number);
    }
    if (json.street) {
      this.street = String(json.street);
    }
    if (json.additional) {
      this.additional = String(json.additional);
    }
    if (json.city) {
      this.city = String(json.city);
    }
    if (json.state) {
      this.state = String(json.state);
    }
    if (json.postal_code) {
      this.postalCode = String(json.postal_code);
    }
    if (json.device_settings_used) {
      this.deviceSettingsUsed = String(json.device_settings_used);
    }
    if (json.number_of_treatments_performed != null && json.number_of_treatments_performed !== undefined) {
      this.numberOfTreatmentsPerformed = Number(json.number_of_treatments_performed);
    }
    if (json.accessories && Array.isArray(json.accessories)) {
      this.accessories = json.accessories.map((item) => Taxonomy.fromJSON(item));
    }
    if (json.instagram_url !== undefined && json.instagram_url !== null) {
      this.instagramUrl = String(json.instagram_url);
    }
    if (json.facebook_url !== undefined && json.facebook_url !== null) {
      this.facebookUrl = String(json.facebook_url);
    }
    if (json.tiktok_url !== undefined && json.tiktok_url !== null) {
      this.tiktokUrl = String(json.tiktok_url);
    }
  }
  public toJSON(context: toJSONContext = 'server') {
    const json = super.toJSON(context);
    if (context !== 'server') {
      return json;
    }
    const { practice, product, products, ...other } = json;
    const out: BeforeAfterJSON = { ...other };
    if (practice) {
      out.practice_id = practice.id;
    }
    if (this.products && this.products.length) {
      out.products = this.products.map((p) => ({ id: p.id, title: p.title }));
      out.product_id = this.products[0].id;
    } else if (product) {
      out.product_id = product.id;
      out.product = product;
    }
    return out;
  }
}
